import { ChevronRight } from 'lucide-react';
import { cn } from '../lib/cn.ts';

/**
 * One indentation rule for every grouped list (spacing-layout.md, "Groups"): a group header
 * reads chevron, icon, label, and its rows sit one step in, so each row's leading icon lines up
 * under the header's icon, never under the chevron.
 *
 * The step is the chevron's 16 px slot plus the header's 8 px gap: 24 px, `space-6`. It holds
 * when the header and its rows share the same left padding (`pl-row-x` on both).
 *
 *   <header className="flex h-row items-center gap-2 pl-row-x">
 *     <GroupChevron open={open} /> <WorkStateIcon state="running" /> Running 4
 *   </header>
 *   <ul className={GROUP_INDENT}>{rows}</ul>
 */

/** Padding that sets a group's rows one step in: on the element holding the rows. */
export const GROUP_INDENT = 'pl-6';

/** The same step as a margin: for a row placed on its own (an absolutely positioned, windowed row). */
export const GROUP_INDENT_SHIFT = 'ml-6';

export interface GroupChevronProps {
  /** The group is expanded: the chevron points down. */
  open: boolean;
  className?: string;
}

/** A group header's fold chevron in its 16 px slot, so the header's icon lands one indent step in. */
export function GroupChevron({ open, className }: GroupChevronProps) {
  return (
    <span data-group-chevron="" aria-hidden className={cn('inline-flex size-4 shrink-0 items-center justify-center text-fg-subtle', className)}>
      <ChevronRight className={cn('size-3.5 transition-transform duration-(--duration-fast)', open && 'rotate-90')} />
    </span>
  );
}
